/* =============================================================================
   CORE / LOGO-COLORS — Brand-Farb-Extraktion aus dem Logo (S167 P4)
   =============================================================================
   Beim Logo-Upload werden dominante + Akzentfarbe automatisch abgeleitet und
   in `settings.brand.{primaryColor, accentColor}` VORGESCHLAGEN (manuell
   überschreibbar, kein Clobber bei jedem PUT — siehe settings-restaurant.js).

   Aufbau:
     - Reine Farb-Utils (hex/rgb/hsv, WCAG-Luminanz + Kontrast)  → unit-getestet
     - Quantisierung von Pixel-Daten (Histogramm-Buckets)        → unit-getestet
     - Brand-Pick-Heuristik (dominant + komplementärer Akzent)   → unit-getestet
     - Kontrast-Sicherung gegen den dunklen Mail-Hintergrund      → unit-getestet
     - SVG-Farb-Parser (fill / stop-color / style)               → unit-getestet
     - Browser-Wrapper (Canvas/Image/FileReader)                 → DOM-only, defensiv

   Keine externe Lib — Canvas + Histogramm reichen. Niemals throw nach außen:
   Branding ist UX, kein App-Blocker. Bei Fehlern → null, Caller behält Defaults.
============================================================================ */

// -----------------------------------------------------------------------------
// Farb-Utils (pure)
// -----------------------------------------------------------------------------

/** "#rgb" / "#rrggbb" → {r,g,b} (0-255) oder null bei ungültigem Input. */
export function hexToRgb(hex) {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16)
  };
}

/** (r,g,b) 0-255 → "#rrggbb" (clamp + Rundung). */
export function rgbToHex(r, g, b) {
  const c = (v) => {
    const n = Math.max(0, Math.min(255, Math.round(v)));
    return n.toString(16).padStart(2, '0');
  };
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** (r,g,b) 0-255 → {h:0-360, s:0-1, v:0-1}. */
export function rgbToHsv(r, g, b) {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn)      h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else                 h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

/** {h:0-360, s:0-1, v:0-1} → {r,g,b} 0-255. */
export function hsvToRgb(h, s, v) {
  h = ((h % 360) + 360) % 360;
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0, g = 0, b = 0;
  if (h < 60)       { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else              { r = c; g = 0; b = x; }
  return { r: (r + m) * 255, g: (g + m) * 255, b: (b + m) * 255 };
}

/** WCAG relative Luminanz (0-1) für {r,g,b} 0-255. */
export function relativeLuminance(rgb) {
  if (!rgb) return 0;
  const lin = (v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(rgb.r) + 0.7152 * lin(rgb.g) + 0.0722 * lin(rgb.b);
}

/** WCAG-Kontrastverhältnis (1..21) zwischen zwei Hex-Farben. */
export function contrastRatio(hex1, hex2) {
  const a = hexToRgb(hex1), b = hexToRgb(hex2);
  if (!a || !b) return 1;
  const l1 = relativeLuminance(a), l2 = relativeLuminance(b);
  const hi = Math.max(l1, l2), lo = Math.min(l1, l2);
  return (hi + 0.05) / (lo + 0.05);
}

// -----------------------------------------------------------------------------
// Quantisierung (pure)
// -----------------------------------------------------------------------------

/**
 * Histogramm-Quantisierung über RGBA-Pixel (Uint8ClampedArray oder Array).
 * Bucketing per `bucketBits` (Default 4 → 16 Stufen/Kanal). Transparente und
 * fast-transparente Pixel (alpha < alphaThreshold) werden ignoriert.
 *
 * @returns {Array<{hex,r,g,b,count}>} absteigend nach Häufigkeit.
 */
export function quantizePixels(pixels, opts) {
  opts = opts || {};
  const bucketBits = opts.bucketBits || 4;
  const alphaThreshold = opts.alphaThreshold != null ? opts.alphaThreshold : 128;
  const shift = 8 - bucketBits;            // z.B. 4 → >>4
  const half = (1 << shift) >> 1;          // Bucket-Mitte für die Re-Konstruktion
  if (!pixels || typeof pixels.length !== 'number') return [];
  const buckets = new Map();
  for (let i = 0; i + 3 < pixels.length; i += 4) {
    const a = pixels[i + 3];
    if (a < alphaThreshold) continue;
    const r = pixels[i], g = pixels[i + 1], b = pixels[i + 2];
    const key = ((r >> shift) << (bucketBits * 2)) | ((g >> shift) << bucketBits) | (b >> shift);
    const e = buckets.get(key);
    if (e) { e.count++; e.r += r; e.g += g; e.b += b; }
    else   { buckets.set(key, { count: 1, r, g, b }); }
  }
  const out = [];
  for (const e of buckets.values()) {
    // Mittelwert der Bucket-Pixel = repräsentativere Farbe als die Bucket-Mitte.
    const r = Math.round(e.r / e.count);
    const g = Math.round(e.g / e.count);
    const b = Math.round(e.b / e.count);
    out.push({ hex: rgbToHex(r, g, b), r, g, b, count: e.count });
  }
  out.sort((x, y) => y.count - x.count);
  return out;
}

// -----------------------------------------------------------------------------
// Brand-Pick-Heuristik (pure)
// -----------------------------------------------------------------------------

const DEFAULT_PRIMARY = '#2f6b3a';
const DEFAULT_ACCENT  = '#d97a2c';

/** Ist die Farbe „chromatisch" genug, um als Markenfarbe zu taugen?
 *  Schließt Weiß/Schwarz/Grau (zu geringe Sättigung) sowie fast-Weiß/-Schwarz aus. */
function _isChromatic(c) {
  const { s, v } = rgbToHsv(c.r, c.g, c.b);
  return s >= 0.18 && v >= 0.12 && v <= 0.97;
}

/** Score = Häufigkeit, gewichtet mit Sättigung (vivide Farben bevorzugt). */
function _vividScore(c) {
  const { s } = rgbToHsv(c.r, c.g, c.b);
  return c.count * (0.4 + 0.6 * s);
}

/** Kürzeste Hue-Distanz (0-180) zwischen zwei Farben. */
function _hueDistance(a, b) {
  const ha = rgbToHsv(a.r, a.g, a.b).h;
  const hb = rgbToHsv(b.r, b.g, b.b).h;
  const d = Math.abs(ha - hb) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Wählt {primary, accent} aus einer quantisierten Palette.
 *  - primary = häufigste/vivideste chromatische Farbe.
 *  - accent  = häufigste verbleibende Farbe mit deutlicher Hue-Distanz
 *              (>= minHueDist) zur Primärfarbe (Komplementär-/Sekundärton).
 *  Fallbacks: monochrome Logos → primary aus dunkelster Nicht-fast-Weiß-Farbe,
 *  accent = Default. Leere Palette → beide Default.
 *
 * @returns {{primary:string, accent:string, primaryFound:boolean, accentFound:boolean}}
 */
export function pickBrandColors(palette, opts) {
  opts = opts || {};
  const minHueDist = opts.minHueDist != null ? opts.minHueDist : 35;
  const list = Array.isArray(palette) ? palette.filter(c => c && typeof c.r === 'number') : [];
  if (!list.length) {
    return { primary: DEFAULT_PRIMARY, accent: DEFAULT_ACCENT, primaryFound: false, accentFound: false };
  }
  const chromatic = list.filter(_isChromatic).sort((a, b) => _vividScore(b) - _vividScore(a));

  let primary = null, accent = null;
  if (chromatic.length) {
    primary = chromatic[0];
    for (let i = 1; i < chromatic.length; i++) {
      if (_hueDistance(primary, chromatic[i]) >= minHueDist) { accent = chromatic[i]; break; }
    }
    // Kein klar abweichender zweiter Ton → nimm die zweithäufigste chromatische,
    // sonst Default-Akzent.
    if (!accent && chromatic.length > 1) accent = chromatic[1];
  } else {
    // Monochrom (nur Graustufen): dunkelste Fläche, die nicht fast-weiß ist,
    // als „primary". Pures Schwarz nur als letzter Ausweg.
    const sorted = list.slice().sort((a, b) =>
      relativeLuminance(a) - relativeLuminance(b));
    const nonWhite = sorted.filter(c => relativeLuminance(c) < 0.7);
    primary = nonWhite.find(c => relativeLuminance(c) > 0.004) || nonWhite[0] || sorted[0];
  }

  return {
    primary: primary ? rgbToHex(primary.r, primary.g, primary.b) : DEFAULT_PRIMARY,
    accent:  accent  ? rgbToHex(accent.r, accent.g, accent.b)   : DEFAULT_ACCENT,
    primaryFound: !!primary,
    accentFound:  !!accent
  };
}

// -----------------------------------------------------------------------------
// Kontrast-Sicherung gegen dunklen Hintergrund (pure)
// -----------------------------------------------------------------------------

/**
 * Stellt sicher, dass `hex` als Text/Akzent auf `bgHex` (z.B. dunkler Mail-
 * Hintergrund #1a1d1f) mindestens `minRatio` Kontrast hat. Falls nicht, wird
 * die Farbe schrittweise aufgehellt (HSV-Value rauf, Sättigung leicht runter),
 * ohne den Farbton zu verlieren. Gibt die (ggf. angepasste) Hex zurück.
 */
export function ensureContrastOnDark(hex, bgHex, minRatio) {
  const min = minRatio != null ? minRatio : 3.0;
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  if (contrastRatio(hex, bgHex) >= min) return hex;
  let { h, s, v } = rgbToHsv(rgb.r, rgb.g, rgb.b);
  let cur = hex;
  for (let i = 0; i < 24; i++) {
    v = Math.min(1, v + 0.04);
    s = Math.max(0, s - 0.012);
    const next = hsvToRgb(h, s, v);
    cur = rgbToHex(next.r, next.g, next.b);
    if (contrastRatio(cur, bgHex) >= min) return cur;
    if (v >= 1 && s <= 0) break;
  }
  return cur;
}

/**
 * Dunklere Variante einer Hex-Farbe (HSV-Value runter, Sättigung leicht rauf
 * für sattere Tiefe). `amount` 0-1 = relativer Value-Abzug (Default 0.22).
 * Wird genutzt, um aus brand.primaryColor/accentColor die `-dark`-Token-Varianten
 * abzuleiten (z. B. Guest-Popup-Titel, aktive Service-Chips).
 */
export function darkenHex(hex, amount) {
  const a = amount != null ? amount : 0.22;
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  let { h, s, v } = rgbToHsv(rgb.r, rgb.g, rgb.b);
  v = Math.max(0, v * (1 - a));
  s = Math.min(1, s + 0.05);
  const out = hsvToRgb(h, s, v);
  return rgbToHex(out.r, out.g, out.b);
}

// -----------------------------------------------------------------------------
// SVG-Farb-Parser (pure)
// -----------------------------------------------------------------------------

/**
 * Extrahiert Hex-Farben aus SVG-Quelltext: `fill="#.."`, `stop-color="#.."`,
 * sowie `style="...fill:#..;stop-color:#.."`. Ignoriert `none`/`currentColor`.
 * Liefert eine Pseudo-Palette [{hex,r,g,b,count}] (count = Vorkommen), damit
 * `pickBrandColors` direkt darauf laufen kann.
 */
export function parseSvgColors(svgText) {
  if (typeof svgText !== 'string' || !svgText) return [];
  const counts = new Map();
  const add = (raw) => {
    const rgb = hexToRgb(raw);
    if (!rgb) return;
    const hex = rgbToHex(rgb.r, rgb.g, rgb.b);
    counts.set(hex, (counts.get(hex) || 0) + 1);
  };
  // Attribut- und Style-basierte Hex-Farben (fill / stroke / stop-color).
  const re = /(?:fill|stroke|stop-color)\s*[:=]\s*["']?\s*(#[0-9a-fA-F]{3,6})/g;
  let m;
  while ((m = re.exec(svgText)) !== null) add(m[1]);
  const out = [];
  for (const [hex, count] of counts.entries()) {
    const rgb = hexToRgb(hex);
    out.push({ hex, r: rgb.r, g: rgb.g, b: rgb.b, count });
  }
  out.sort((a, b) => b.count - a.count);
  return out;
}

// -----------------------------------------------------------------------------
// Browser-Wrapper (DOM/Canvas — defensiv, nicht unit-getestet)
// -----------------------------------------------------------------------------

/** Zeichnet ein <img> auf ein Off-screen-Canvas (downscaled) und quantisiert.
 *  Wirft NICHT — bei Canvas-Taint (cross-origin) oder fehlendem DOM → null. */
export async function extractPaletteFromImage(imgEl, maxDim) {
  try {
    if (typeof document === 'undefined' || !imgEl) return null;
    const dim = maxDim || 64;
    const w = imgEl.naturalWidth || imgEl.width;
    const h = imgEl.naturalHeight || imgEl.height;
    if (!w || !h) return null;
    const scale = Math.min(1, dim / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(imgEl, 0, 0, cw, ch);
    const data = ctx.getImageData(0, 0, cw, ch).data; // taint → throws
    return quantizePixels(data);
  } catch (_) {
    return null; // cross-origin-taint o.ä. — Caller behält Defaults
  }
}

/** Lädt eine URL als <img> (crossOrigin anonymous für Canvas-Lesbarkeit). */
function _loadImage(url) {
  return new Promise((resolve, reject) => {
    try {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Image load failed'));
      img.src = url;
    } catch (e) { reject(e); }
  });
}

function _readBlobAsText(blob) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result || ''));
    fr.onerror = () => reject(fr.error || new Error('FileReader error'));
    fr.readAsText(blob);
  });
}

/**
 * Hauptzugang: leitet {primary, accent} aus einem Logo ab. `source` kann sein:
 *   - File/Blob (frischer Upload — wird client-seitig gelesen, kein Re-Fetch)
 *   - URL-String (bestehendes Logo / Default-Asset)
 * SVG → Farb-Parse; Raster → Canvas-Quantisierung.
 *
 * @returns {Promise<{primary,accent,primaryFound,accentFound,source}|null>}
 */
export async function extractLogoColors(source, opts) {
  try {
    if (!source) return null;
    const isBlob = (typeof Blob !== 'undefined' && source instanceof Blob);
    const type = isBlob ? (source.type || '') : '';
    const name = isBlob ? (source.name || '') : String(source);
    const isSvg = /svg/i.test(type) || /\.svg(\?|#|$)/i.test(name);

    if (isSvg) {
      const text = isBlob ? await _readBlobAsText(source)
        : await (await fetch(source)).text();
      const palette = parseSvgColors(text);
      if (!palette.length) return null;
      return { ...pickBrandColors(palette, opts), source: 'svg' };
    }

    // Raster: Object-URL bei Blob, sonst direkte URL.
    let url = isBlob ? URL.createObjectURL(source) : source;
    try {
      const img = await _loadImage(url);
      const palette = await extractPaletteFromImage(img, opts && opts.maxDim);
      if (!palette || !palette.length) return null;
      return { ...pickBrandColors(palette, opts), source: 'raster' };
    } finally {
      if (isBlob && typeof URL !== 'undefined' && URL.revokeObjectURL) {
        try { URL.revokeObjectURL(url); } catch (_) { /* ignore */ }
      }
    }
  } catch (_) {
    return null; // niemals throw — Branding ist UX
  }
}
